import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Typography, List, Tag, Spin, Empty } from 'antd'
import {
  MedicineBoxOutlined,
  AlertOutlined,
  HeartOutlined,
  ScissorOutlined,
  TeamOutlined,
  CoffeeOutlined
} from '@ant-design/icons'
import { getPatient } from '../../Medical History/utils/utils'

const { Text } = Typography

// Inline styles for custom scrollbar
const scrollbarStyle = {
  '&::-webkit-scrollbar': {
    width: '6px'
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px'
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8'
  }
}

const scrollableContainerStyle = {
  maxHeight: '150px',
  overflowY: 'auto',
  paddingRight: '4px',
  ...scrollbarStyle
}

const PatientInfoMedHist = ({ userRef }) => {
  const [loading, setLoading] = useState(true)
  const [medicalHistory, setMedicalHistory] = useState(null)

  useEffect(() => {
    const fetchMedicalHistory = async () => {
      if (!userRef) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const patientData = await getPatient(userRef)
        console.log('Fetched patient data:', patientData) // Debug log

        setMedicalHistory(patientData.medicalHistory || {})
      } catch (error) {
        console.error('Error fetching medical history:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMedicalHistory()
  }, [userRef])

  // Helper function to check if a section has data
  const hasData = (section) => {
    if (!medicalHistory || !medicalHistory[section]) return false

    if (section === 'allergies') {
      const allergies = medicalHistory.allergies
      return (
        (allergies?.meds && allergies.meds.length > 0) ||
        (allergies?.food && allergies.food.length > 0) ||
        (allergies?.other && allergies.other.length > 0)
      )
    }

    if (section === 'chronicIllnesses') {
      return medicalHistory.chronicIllnesses?.list && medicalHistory.chronicIllnesses.list.length > 0
    }

    if (section === 'medications') {
      return medicalHistory.medications?.list && medicalHistory.medications.list.length > 0
    }

    if (section === 'surgicalHistory') {
      return medicalHistory.surgicalHistory?.list && medicalHistory.surgicalHistory.list.length > 0
    }

    if (section === 'familyHistory') {
      return medicalHistory.familyHistory?.list && medicalHistory.familyHistory.list.length > 0
    }

    if (section === 'lifestyle') {
      return (
        medicalHistory.lifestyle && (medicalHistory.lifestyle.smoking || medicalHistory.lifestyle.alcoholConsumption)
      )
    }

    return false
  }

  // Allergies Section
  const AllergiesSection = () => {
    if (!hasData('allergies')) return null

    const { meds = [], food = [], other = [] } = medicalHistory.allergies

    return (
      <Card
        title={
          <>
            <AlertOutlined /> Allergies
          </>
        }
        style={{ marginBottom: 16 }}
        size='small'
      >
        {meds.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Medication Allergies:</Text>
            <div style={scrollableContainerStyle}>
              <List
                dataSource={meds}
                renderItem={(item) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Tag color='red'>{item.name || item}</Tag>
                    {item.reaction && <Text type='secondary'> - {item.reaction}</Text>}
                    {item.severity && <Text type='secondary'> ({item.severity})</Text>}
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}

        {food.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Food Allergies:</Text>
            <div style={scrollableContainerStyle}>
              <List
                dataSource={food}
                renderItem={(item) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Tag color='orange'>{item}</Tag>
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}

        {other.length > 0 && (
          <div>
            <Text strong>General Allergies:</Text>
            <div style={scrollableContainerStyle}>
              <List
                dataSource={other}
                renderItem={(item) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Tag color='purple'>{item}</Tag>
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}

        {!meds.length && !food.length && !other.length && <Empty description='No allergies recorded' />}
      </Card>
    )
  }

  // Chronic Illnesses Section
  const ChronicIllnessesSection = () => {
    if (!hasData('chronicIllnesses')) return null

    return (
      <Card
        title={
          <>
            <HeartOutlined /> Chronic Conditions
          </>
        }
        style={{ marginBottom: 16 }}
        size='small'
      >
        <div style={{ ...scrollableContainerStyle, maxHeight: '150px' }}>
          <List
            dataSource={medicalHistory.chronicIllnesses.list}
            renderItem={(item) => (
              <List.Item style={{ padding: '4px 0' }}>
                <Tag color='purple'>{item.name || item}</Tag>
                {item.diagnosisDate && <Text type='secondary'> - Diagnosed: {item.diagnosisDate}</Text>}
              </List.Item>
            )}
            locale={{ emptyText: <Empty description='No chronic conditions recorded' /> }}
          />
        </div>
      </Card>
    )
  }

  // Surgical History Section
  const SurgicalHistorySection = () => {
    if (!hasData('surgicalHistory')) return null

    return (
      <Card
        title={
          <>
            <ScissorOutlined /> Surgical History
          </>
        }
        style={{ marginBottom: 16 }}
        size='small'
      >
        <div style={{ ...scrollableContainerStyle, maxHeight: '150px' }}>
          <List
            dataSource={medicalHistory.surgicalHistory.list}
            renderItem={(item) => (
              <List.Item style={{ padding: '4px 0' }}>
                <Tag color='cyan'>{item.procedure || item.name || item}</Tag>
                {item.date && <Text type='secondary'> - Date: {new Date(item.date).toLocaleDateString()}</Text>}
                {item.notes && <Text type='secondary'> - {item.notes}</Text>}
              </List.Item>
            )}
            locale={{ emptyText: <Empty description='No surgical history recorded' /> }}
          />
        </div>
      </Card>
    )
  }

  // Medications Section
  const MedicationsSection = () => {
    if (!hasData('medications')) return null

    // Group medications by type
    const medications = medicalHistory.medications.list || []
    const acute = medications.filter((med) => med.type === 'acute')
    const chronic = medications.filter((med) => med.type === 'chronic')

    return (
      <Card
        title={
          <>
            <MedicineBoxOutlined /> Current Medications
          </>
        }
        style={{ marginBottom: 16 }}
        size='small'
      >
        {chronic.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Chronic Medications:</Text>
            <div style={scrollableContainerStyle}>
              <List
                dataSource={chronic}
                renderItem={(item) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Tag color='green'>{item.name}</Tag>
                    {item.dosage && <Text type='secondary'> - Dosage: {item.dosage}</Text>}
                    {item.frequency && <Text type='secondary'> ({item.frequency})</Text>}
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}

        {acute.length > 0 && (
          <div>
            <Text strong>Acute Medications:</Text>
            <div style={scrollableContainerStyle}>
              <List
                dataSource={acute}
                renderItem={(item) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Tag color='cyan'>{item.name}</Tag>
                    {item.dosage && <Text type='secondary'> - Dosage: {item.dosage}</Text>}
                    {item.frequency && <Text type='secondary'> ({item.frequency})</Text>}
                  </List.Item>
                )}
              />
            </div>
          </div>
        )}

        {!chronic.length && !acute.length && <Empty description='No medications recorded' />}
      </Card>
    )
  }

  // Family History Section
  const FamilyHistorySection = () => {
    if (!hasData('familyHistory')) return null

    return (
      <Card
        title={
          <>
            <TeamOutlined /> Family History
          </>
        }
        style={{ marginBottom: 16 }}
        size='small'
      >
        <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
          <List
            dataSource={medicalHistory.familyHistory.list}
            renderItem={(item) => (
              <List.Item style={{ padding: '4px 0' }}>
                <Tag color='orange'>{item}</Tag>
              </List.Item>
            )}
            locale={{ emptyText: <Empty description='No family history recorded' /> }}
          />
        </div>
      </Card>
    )
  }

  // Lifestyle Section
  const LifestyleSection = () => {
    if (!hasData('lifestyle')) return null

    const { smoking, alcoholConsumption, smokingLevel, alcoholConsumptionLevel } = medicalHistory.lifestyle

    return (
      <Card
        title={
          <>
            <CoffeeOutlined /> Lifestyle
          </>
        }
        style={{ marginBottom: 16 }}
        size='small'
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text strong>Smoking: </Text>
            <Text>{smoking ? `Yes${smokingLevel ? ` - ${smokingLevel}` : ''}` : 'No'}</Text>
          </Col>

          <Col span={24}>
            <Text strong>Alcohol: </Text>
            <Text>
              {alcoholConsumption ? `Yes${alcoholConsumptionLevel ? ` - ${alcoholConsumptionLevel}` : ''}` : 'No'}
            </Text>
          </Col>
        </Row>
      </Card>
    )
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size='large' />
        <div style={{ marginTop: 16 }}>Loading medical history...</div>
      </div>
    )
  }

  if (!medicalHistory) {
    return <Empty description='No medical history available for this patient' style={{ padding: '50px' }} />
  }

  const hasMedicalData =
    hasData('allergies') ||
    hasData('chronicIllnesses') ||
    hasData('medications') ||
    hasData('surgicalHistory') ||
    hasData('familyHistory') ||
    hasData('lifestyle')

  if (!hasMedicalData) {
    return <Empty description='No medical history data available for this patient' style={{ padding: '50px' }} />
  }

  return (
    <div
      style={{
        padding: '0 16px',
        height: '100%',
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      <AllergiesSection />
      <ChronicIllnessesSection />
      <MedicationsSection />
      <SurgicalHistorySection />
      <FamilyHistorySection />
      <LifestyleSection />
    </div>
  )
}

export default PatientInfoMedHist
